import QRCode from "qrcode";
import "./hexchain.css";
import { beginHexchainAnimation, beginHexchainGesture, endHexchainAnimation, endHexchainGesture, resetHexchainInteraction } from "./hexchain-interaction";

export type HexchainTile = { color: number; age: number; frozen: boolean; special?: "wild" };
type HexchainBoardTransition = {
  board: HexchainTile[];
  clearedIndexes: number[];
  moved: Array<{ from: number; to: number }>;
  spawnedIndexes: number[];
};
type HexchainAnimation = {
  before: HexchainTile[];
  primary: HexchainBoardTransition;
  wipe?: HexchainBoardTransition & { highlightedIndexes: number[] };
  frost?: { before: HexchainTile[]; after: HexchainTile[]; landedIndexes: number[] };
  chainColor?: number;
  resetKind?: "color" | "all" | "shuffle";
  resetIndexes: number[];
};
export type HexchainPlayerView = {
  id: string;
  name: string;
  board: HexchainTile[];
  score: number;
  chains: number;
  bestChain: number;
  fiveStreak: number;
  incomingFrost: number;
  shuffleLeft: boolean;
  alive: boolean;
  stuck: boolean;
};
export type HexchainView = {
  status: "lobby" | "battle" | "finished";
  durationMs: number;
  startedAt: number | null;
  deadline: number | null;
  players: Record<string, HexchainPlayerView>;
  playerOrder: string[];
  winnerIds: string[];
  lastEvent: { id: number; kind: string; playerId: string; targetPlayerId?: string; chainLength?: number; gained?: number; baseGained?: number; wipeBonus?: number; previousBestChain?: number; attack?: number; cancelled?: number; landed?: number; path?: number[]; animation?: HexchainAnimation; message: string } | null;
};

type RoomPlayer = { id: string; name: string; score: number; connected: boolean; isAdmin: boolean };
type HexchainContext = {
  roomCode: string;
  isAdmin: boolean;
  playerId: string;
  players: RoomPlayer[];
  state: HexchainView;
  send: (message: object) => void;
  escapeHtml: (value: string) => string;
  initials: (value: string) => string;
  tap: () => void;
  tone: (frequency: number, duration?: number, type?: OscillatorType, delay?: number, volume?: number) => void;
  sweep: (startFrequency: number, endFrequency: number, duration?: number, type?: OscillatorType, delay?: number, volume?: number, filterStart?: number, filterEnd?: number) => void;
  noise: (duration?: number, volume?: number, startFrequency?: number, peakFrequency?: number, endFrequency?: number, delay?: number) => void;
  showToast: (message: string) => void;
  refreshAfterGesture: () => void;
};

const HEXCHAIN_FREEZE_AGE = 7;
const ROW_COUNTS = [4, 5, 6, 7, 6, 5, 4];
const COLORS = [
  ["#7aaad4", "#3f6e95", "#5b8cb8"], ["#78bfa0", "#3d7d5e", "#5a9e7e"], ["#e0c472", "#a68b3a", "#c8a855"],
  ["#d47878", "#943f3f", "#b85c5c"], ["#a888c8", "#6d4f8c", "#8b6baa"], ["#e09570", "#a05c38", "#c47850"],
];
const CELLS = (() => {
  const cells: Array<{ x: number; y: number; radius: number }> = [];
  ROW_COUNTS.forEach((count, row) => {
    const startX = 371 - ((count - 1) * 96) / 2;
    for (let column = 0; column < count; column += 1) cells.push({ x: startX + column * 96, y: 339 + (row - 3) * 83, radius: 50 });
  });
  return cells;
})();
const ADJACENCY = CELLS.map((cell, index) => CELLS.map((candidate, candidateIndex) => ({ candidateIndex, distance: Math.hypot(cell.x - candidate.x, cell.y - candidate.y) }))
  .filter(({ candidateIndex, distance }) => candidateIndex !== index && distance < 106).map(({ candidateIndex }) => candidateIndex));

let selectedTargetId = "";
let countdownTimer: number | null = null;
let boardAnimationFrame: number | null = null;
let chainCalloutTimer: number | null = null;
let achievementToastTimer: number | null = null;
const boardMessageTimers = new Set<number>();
const animatedEvents = new Map<string, number>();

const CHAIN_WORDS = [
  "Legendary!", "Incredible!", "Brilliant!", "Unbelievable!", "Insane!",
  "Epic!", "Masterful!", "Superb!", "Outstanding!", "Phenomenal!",
  "Spectacular!", "Immaculate!", "Flawless!", "Perfect!", "Elite!",
  "Iconic!", "Majestic!", "Pristine!", "Glorious!", "Genius!",
  "Big brain!", "200 IQ!", "Chess move!", "Calculated!", "Surgical!",
  "Clinical!", "Precision!", "Tactical!", "Next-level thinking!",
  "Found the angle!", "What a read!", "Clean sequence!",
];

export function hexchainTileHtml(attributes: string) {
  return `<button class="game-tile game-tile--hexchain" ${attributes}>
    <span class="game-tile__art hexchain-tile-art" aria-hidden="true"><i></i><i></i><i></i><i></i><i></i><b>VS</b></span>
    <span class="game-tile__copy"><small>NEW · SOLO OR LIVE BATTLE</small><strong>Hexchain Siege</strong><em>Chase an untimed solo high score or link colors live to freeze your rivals.</em></span>
    <span class="game-tile__go" aria-hidden="true">→</span>
  </button>`;
}

export function hexchainDuelResultHtml(state: HexchainView, playerId: string) {
  if (state.status !== "finished" || state.playerOrder.length !== 2 || !state.winnerIds.length) return "";
  const player = state.players[playerId];
  const opponentId = state.playerOrder.find((id) => id !== playerId);
  const opponent = opponentId ? state.players[opponentId] : null;
  if (!player || !opponent) return "";
  const tied = state.winnerIds.length > 1;
  const outcome = tied ? "tie" : state.winnerIds.includes(playerId) ? "win" : "loss";
  const message = outcome === "win" ? "You won the game!" : outcome === "loss" ? "You lost the game" : "The game is a tie!";
  const timedOut = state.playerOrder.every((id) => state.players[id]?.alive);
  return `<section class="hexchain-duel-result is-${outcome}" role="status" aria-live="assertive">
    <span>${timedOut ? "TIME’S UP" : "GAME OVER"}</span>
    <strong>${message}</strong>
    <small>Final score · ${player.score.toLocaleString()} – ${opponent.score.toLocaleString()}</small>
  </section>`;
}

export function cleanupHexchain() {
  if (countdownTimer !== null) window.clearInterval(countdownTimer);
  if (boardAnimationFrame !== null) window.cancelAnimationFrame(boardAnimationFrame);
  if (chainCalloutTimer !== null) window.clearTimeout(chainCalloutTimer);
  if (achievementToastTimer !== null) window.clearTimeout(achievementToastTimer);
  boardMessageTimers.forEach((timer) => window.clearTimeout(timer));
  boardMessageTimers.clear();
  countdownTimer = null;
  boardAnimationFrame = null;
  chainCalloutTimer = null;
  achievementToastTimer = null;
  resetHexchainInteraction();
}

function instructionsButtonHtml() {
  return `<button id="hexchain-help" class="hexchain-help" type="button" aria-haspopup="dialog" aria-controls="hexchain-how-to">Instructions</button>`;
}

function instructionsHtml(roomCode: string) {
  // Keep the guide open at the same position when a live room update redraws the page.
  const previous = typeof document === "undefined" ? null : document.querySelector<HTMLDialogElement>("#hexchain-how-to");
  const reopen = previous?.open && previous.dataset.roomCode === roomCode;
  return `<dialog class="hexchain-how-to" id="hexchain-how-to" aria-labelledby="hexchain-how-to-title" data-room-code="${roomCode}" ${reopen ? `data-reopen="${previous.scrollTop}"` : ""}>
    <div class="hexchain-how-to__card">
      <header class="hexchain-how-to__header">
        <p class="eyebrow">HEXCHAIN SIEGE</p>
        <h2 id="hexchain-how-to-title">Instructions</h2>
        <button id="hexchain-help-close" class="hexchain-how-to__close" type="button" aria-label="Close instructions" autofocus>×</button>
      </header>
      <section>
        <h3>1. Your goal &amp; game modes</h3>
        <p>Link matching gems on your own 37-tile board, score points, and keep enough tiles playable to survive.</p>
        <ul>
          <li><b>SOLO</b><span>Start with one player for an <strong>untimed high-score run</strong>. There are no rival frost attacks. Your run ends when you have no legal chain and cannot recover with Shuffle.</span></li>
          <li><b>VS</b><span>With two or more players, everyone plays <strong>at the same time for 90 seconds</strong> on their own board. There is no waiting for turns. All players start with the same layout.</span></li>
        </ul>
        <p>The room admin starts the game. Open these instructions whenever you need a refresher; <strong>the multiplayer timer keeps running</strong>.</p>
      </section>
      <section>
        <h3>2. How to make a chain</h3>
        <ol>
          <li>Press a gem with your mouse or finger and <strong>drag through touching tiles of the same color</strong>. Each next tile must be a neighbor of the last one; you cannot jump over gaps.</li>
          <li>Link <strong>at least 3 tiles</strong>, then release to submit the chain. Each tile can appear only once in your path. Drag back to the previous tile to undo the last link.</li>
          <li>Your chain clears, the remaining tiles settle downward, and new gems fill the gaps. Releasing a path shorter than 3 does not score or advance the freeze countdown.</li>
        </ol>
        <p><strong>Wild tiles</strong> are the glowing rainbow gems. They match any one color and never freeze, including from rival attacks. You can use one to bridge gems of the same color, but it cannot join two different ordinary colors in one chain. The first wild arrives after your eighth successful chain, then another after every 8–10 successful chains.</p>
      </section>
      <section>
        <h3>3. Scoring &amp; streak multipliers</h3>
        <p><strong>Points gained = base chain points × streak multiplier + color-wipe bonus.</strong> Longer chains are worth much more, so fewer long chains can beat many short chains.</p>
        <table class="hexchain-score-table">
          <caption>Base chain score · 3 = 150 points before bonuses</caption>
          <thead><tr><th scope="col">Chain length</th><th scope="col">Base points</th><th scope="col">Frost sent</th></tr></thead>
          <tbody>
            <tr><th scope="row">3</th><td>150</td><td>0</td></tr>
            <tr><th scope="row">4</th><td>400</td><td>1</td></tr>
            <tr><th scope="row">5</th><td>850</td><td>2</td></tr>
            <tr><th scope="row">6</th><td>1,500</td><td>3</td></tr>
            <tr><th scope="row">7</th><td>2,500</td><td>4</td></tr>
            <tr><th scope="row">8</th><td>4,000</td><td>4</td></tr>
            <tr><th scope="row">9</th><td>6,500</td><td>5</td></tr>
            <tr><th scope="row">10</th><td>10,000</td><td>5</td></tr>
          </tbody>
        </table>
        <p>For 11+ tiles, base points are 10,000 × 1.6 raised to the number of tiles beyond 10, rounded down: <strong>11 = 16,000</strong>, <strong>12 = 25,600</strong>, and so on.</p>
        <p><strong>Consecutive chains of 5 or more</strong> build your streak. The first earns 1× base points, the second 2×, the third 3×, the fourth 4×, and so on with no cap. A 3- or 4-chain resets the streak to 1×; Shuffle also resets it.</p>
        <p><strong>Color-wipe bonus:</strong> a chain of 4+ triggers a second clear after the first refill. Every ordinary tile of the chain’s color is removed, including frozen tiles and newly spawned gems of that color. Each extra tile cleared earns <strong>50 points</strong>. This bonus is added after the multiplier and is not multiplied. Wild tiles are spared by the color wipe.</p>
        <p class="hexchain-score-example"><strong>Example:</strong> your second consecutive 5-chain earns 850 × 2 = 1,700 base points. If the color wipe clears 6 extra tiles, add 6 × 50 = 300, for <strong>2,000 points total</strong>.</p>
      </section>
      <section>
        <h3>4. Freeze countdown &amp; long-chain powers</h3>
        <p>Each successful chain ages the ordinary gems left on your board by one step. Moving down the board does not reset a gem’s age. New gems start fresh. Untouched gems show <strong>2</strong> after 5 chains, <strong>1</strong> after 6, then <strong>❄</strong> after 7. Frozen gems cannot be included in a chain.</p>
        <ul>
          <li><b>4+</b><span><strong>Wipe that color</strong> after the initial clear and refill, earning the bonus described above.</span></li>
          <li><b>6+</b><span><strong>Reset that color:</strong> reset the age and thaw remaining gems of your chain’s color before this move’s aging and refill.</span></li>
          <li><b>7+</b><span><strong>Reset the whole board:</strong> reset the age and thaw every remaining gem before this move’s aging and refill. This gives you breathing room, though incoming frost can still land afterward.</span></li>
        </ul>
      </section>
      <section>
        <h3>5. Rival attacks &amp; defending against frost</h3>
        <p>In multiplayer, a 4+ chain sends frost to one living rival, as shown in the table. For 7+ chains, attacks start at 4 frost and gain 1 more for every 2 extra linked tiles: <strong>11–12 sends 6</strong>, <strong>13–14 sends 7</strong>, and so on. The streak multiplier affects points, not frost strength.</p>
        <p>With two players, attacks go to your opponent automatically. In larger games, tap a rival’s card to choose your target; the game initially selects the highest-scoring living rival.</p>
        <p>Received frost waits in your <strong>INCOMING</strong> counter until your next successful chain. That chain cancels up to <strong>chain length − 3</strong> frost: a 3-chain cancels 0, a 4-chain cancels 1, a 5-chain cancels 2, and so on. After your clears and resets, each remaining frost immediately freezes one random unfrozen ordinary tile. If there are too few eligible tiles, the excess stays queued. Your chain still sends its own attack.</p>
        <p><strong>Defense example:</strong> with 4 incoming frost, a 5-chain cancels 2; the other 2 freeze tiles after the clear. A 7-chain cancels all 4.</p>
      </section>
      <section>
        <h3>6. Shuffle, elimination &amp; winning</h3>
        <p>You get <strong>one Shuffle per game</strong>, available whenever you are still alive. It rearranges unfrozen tiles, clears all queued incoming frost, and resets your streak. Frozen tiles <strong>stay frozen in place</strong>; existing gem ages are preserved, and Shuffle does not score or count as a chain.</p>
        <p>If there is no legal 3+ chain, use Shuffle if you still have it. If Shuffle cannot uncover a chain, or no legal chain remains after you have used it, you are <strong>frozen out</strong> and can watch the remaining players.</p>
        <ul>
          <li><b>WIN</b><span>In multiplayer, the <strong>last surviving player wins immediately</strong>, even if another player scored more before being eliminated.</span></li>
          <li><b>TIME</b><span>If 90 seconds expire with several survivors, the <strong>highest score among surviving players wins</strong>. Equal highest scores share the win.</span></li>
          <li><b>SCORE</b><span>In solo, your total when the run ends is your final score. <strong>CHAINS</strong> counts successful clears, <strong>BEST</strong> is your longest submitted chain, and <strong>STREAK</strong> shows the current multiplier.</span></li>
        </ul>
        <p>Look for 5+ chains to grow your score, 7+ chains to rescue a freezing board, and save Shuffle for a difficult layout or a large incoming attack.</p>
      </section>
      <button id="hexchain-help-got-it" class="hexchain-how-to__got-it" type="button">Got it</button>
    </div>
  </dialog>`;
}

function bindInstructions() {
  const dialog = document.querySelector<HTMLDialogElement>("#hexchain-how-to");
  const button = document.querySelector<HTMLButtonElement>("#hexchain-help");
  if (!dialog || !button) return;
  const close = () => dialog.close();
  button.addEventListener("click", () => dialog.showModal());
  document.querySelector("#hexchain-help-close")?.addEventListener("click", close);
  document.querySelector("#hexchain-help-got-it")?.addEventListener("click", close);
  dialog.addEventListener("click", (event) => { if (event.target === dialog) close(); });
  // Native dialog handles Escape, focus containment, and making the background inert.
  dialog.addEventListener("close", () => button.focus({ preventScroll: true }));
  if (dialog.dataset.reopen !== undefined) {
    dialog.showModal();
    dialog.scrollTop = Number(dialog.dataset.reopen);
  }
}

export function hexchainLobbyHtml(context: HexchainContext) {
  cleanupHexchain();
  const connected = context.players.filter((player) => player.connected);
  const canStart = connected.length >= 1;
  const solo = connected.length === 1;
  return `<section class="hexchain-lobby">
    <div class="hexchain-lobby__tools">${instructionsButtonHtml()}</div>
    <div class="hexchain-lobby__hero">
      <div class="hexchain-mark" aria-hidden="true"><i></i><i></i><i></i><b>✦</b></div>
      <p class="eyebrow">${solo ? "UNTIMED SOLO HIGH-SCORE RUN" : "90-SECOND LIVE PUZZLE BATTLE"}</p>
      <h1>Hexchain <span>Siege</span></h1>
      <p>Build chains on your own board. Longer links fire frost at a rival. On your next chain, length − 3 is countered and the rest immediately freezes tiles.</p>
      <div class="hexchain-room-code"><span>ROOM</span><strong>${context.escapeHtml(context.roomCode)}</strong><button id="hexchain-copy" type="button">Copy</button></div>
    </div>
    <div class="hexchain-lobby__grid">
      <section class="hexchain-rules-card">
        <p class="eyebrow">HOW TO PLAY</p>
        <ol>
          <li><b>Draw a path</b><span>Touch and drag through adjacent tiles of the same color.</span></li>
          <li><b>Chain 3 or more</b><span>Clear and score. Longer chains are worth dramatically more.</span></li>
          <li><b>Watch tiles settle</b><span>After each clear, gems cascade down and new tiles fill every gap.</span></li>
          <li><b>Beat the freeze</b><span>Untouched tiles freeze after 7 of your chains: they show 2, then 1, then ❄. Frozen tiles cannot be chained.</span></li>
          <li><b>Use one Shuffle</b><span>Rearrange the unfrozen tiles once per game. Frozen tiles stay frozen and locked in place.</span></li>
          <li><b>Build a 5+ streak</b><span>Consecutive long chains raise your score multiplier: 2×, 3×, 4× and beyond.</span></li>
        </ol>
        <div class="hexchain-power-row"><span><b>4+</b> wipes that color</span><span><b>5+</b> builds streaks</span><span><b>6+</b> resets that color</span><span><b>7+</b> resets all tiles</span></div>
      </section>
      <section class="hexchain-roster-card">
        <div><p class="eyebrow">CONTENDERS</p><strong>${connected.length} joined</strong></div>
        <ul>${context.players.map((player, index) => `<li class="${player.connected ? "" : "is-away"}"><span class="hexchain-avatar hexchain-avatar--${index % 6}">${context.escapeHtml(context.initials(player.name))}</span><b>${context.escapeHtml(player.name)}</b>${player.id === context.playerId ? "<small>YOU</small>" : ""}${player.isAdmin ? "<small>ADMIN</small>" : ""}</li>`).join("")}</ul>
        <div class="hexchain-join"><canvas id="hexchain-qr" aria-label="QR code to join the room"></canvas><span>Scan to join<br><b>${context.escapeHtml(context.roomCode)}</b></span></div>
      </section>
    </div>
    <div class="hexchain-start-bar">
      <p>${solo ? "Solo mode · play for a high score with no time limit." : canStart ? "The arena is ready · 90-second multiplayer battle." : "Join the room to begin."}</p>
      ${context.isAdmin ? `<button class="primary-button" id="hexchain-start" ${canStart ? "" : "disabled"}><span>${solo ? "Start solo run" : "Start the siege"}</span><b aria-hidden="true">${solo ? "✦" : "⚔"}</b></button>` : `<strong>Waiting for the admin to start…</strong>`}
      <button class="text-button" id="hexchain-leave">Leave room</button>
    </div>
    ${instructionsHtml(context.escapeHtml(context.roomCode))}
  </section>`;
}

export function bindHexchainLobby(context: HexchainContext, leaveRoom: () => void) {
  bindInstructions();
  void QRCode.toCanvas(document.querySelector("#hexchain-qr"), `${location.origin}/?room=${encodeURIComponent(context.roomCode)}`, { width: 132, margin: 1, color: { dark: "#171329", light: "#f8f2df" }, errorCorrectionLevel: "M" });
  document.querySelector("#hexchain-start")?.addEventListener("click", () => { context.tap(); context.send({ type: "start" }); });
  document.querySelector("#hexchain-leave")?.addEventListener("click", leaveRoom);
  document.querySelector("#hexchain-copy")?.addEventListener("click", async () => {
    await navigator.clipboard?.writeText(context.roomCode);
    context.showToast(`Room ${context.roomCode} copied.`);
  });
}

function opponentList(context: HexchainContext) {
  return context.state.playerOrder.map((id) => context.state.players[id]).filter((player) => player && player.id !== context.playerId && player.alive);
}

function setDefaultTarget(context: HexchainContext) {
  const opponents = opponentList(context);
  if (!opponents.some((player) => player.id === selectedTargetId)) selectedTargetId = opponents.sort((first, second) => second.score - first.score)[0]?.id || "";
}

function multiplier(player: HexchainPlayerView) {
  return Math.max(1, player.fiveStreak);
}

function freezeCountdownHtml(player: HexchainPlayerView) {
  let twoTurns = 0;
  let oneTurn = 0;
  player.board.forEach((tile) => {
    if (tile.special === "wild" || tile.frozen) return;
    const movesLeft = HEXCHAIN_FREEZE_AGE - tile.age;
    if (movesLeft === 2) twoTurns += 1;
    if (movesLeft === 1) oneTurn += 1;
  });
  const warnings = [
    oneTurn ? `<span class="is-urgent"><b>1</b> ${oneTurn} marked ${oneTurn === 1 ? "cell freezes" : "cells freeze"} after your next chain.</span>` : "",
    twoTurns ? `<span><b>2</b> ${twoTurns} marked ${twoTurns === 1 ? "cell freezes" : "cells freeze"} in 2 turns.</span>` : "",
  ].filter(Boolean).join("");
  return `<div class="hexchain-freeze-guide ${oneTurn ? "is-urgent" : twoTurns ? "is-warning" : ""}" aria-live="polite">
    <strong>❄ FREEZE COUNTDOWN</strong>
    ${warnings || "<span>Untouched cells warn after 5 chains: <b>2</b> → <b>1</b> → <b>❄</b> on chain 7.</span>"}
  </div>`;
}

export function hexchainGameHtml(context: HexchainContext) {
  cleanupHexchain();
  setDefaultTarget(context);
  const player = context.state.players[context.playerId];
  const opponents = opponentList(context);
  const twoPlayerOpponent = context.state.playerOrder.length === 2
    ? context.state.players[context.state.playerOrder.find((id) => id !== context.playerId) || ""]
    : null;
  if (!player) return `<section class="center-state"><h1>Spectating the siege</h1><p>This match was already underway when you joined.</p></section>`;
  const opponentFrozenTiles = twoPlayerOpponent?.board.filter((tile) => tile.frozen).length || 0;
  const lastGain = context.state.lastEvent?.kind === "chain" && context.state.lastEvent.playerId === player.id ? context.state.lastEvent.gained || 0 : 0;
  return `<section class="hexchain-battle ${player.alive ? "" : "hexchain-battle--eliminated"} ${player.stuck ? "hexchain-battle--stuck" : ""}">
    <header class="hexchain-battle__header">
      <div class="hexchain-header-main"><p class="eyebrow">HEXCHAIN SIEGE · ROOM ${context.escapeHtml(context.roomCode)}</p>${twoPlayerOpponent ? `<section class="hexchain-duel-status" aria-label="Opponent status">
        <div class="hexchain-duel-status__player"><span>${context.escapeHtml(context.initials(twoPlayerOpponent.name))}</span><div><small>OPPONENT</small><strong>${context.escapeHtml(twoPlayerOpponent.name)}</strong></div></div>
        <div><small>SCORE</small><strong>${twoPlayerOpponent.score.toLocaleString()}</strong></div>
        <div><small>FROZEN TILES</small><strong>❄ ${opponentFrozenTiles}</strong></div>
        <div class="${twoPlayerOpponent.incomingFrost ? "is-danger" : ""}"><small>INCOMING</small><strong>❄ ${twoPlayerOpponent.incomingFrost}</strong></div>
      </section>` : ""}</div>
      <div class="hexchain-header-tools">${instructionsButtonHtml()}<div class="hexchain-timer"><span>${context.state.deadline ? "TIME" : "SOLO"}</span><strong id="hexchain-time">${formatTime(context.state.deadline)}</strong></div></div>
    </header>
    ${twoPlayerOpponent ? "" : `<div class="hexchain-opponents" role="group" aria-label="Choose an attack target">
      ${opponents.map((opponent) => `<button class="hexchain-opponent ${opponent.id === selectedTargetId ? "is-targeted" : ""}" data-hexchain-target="${opponent.id}"><span>${context.escapeHtml(context.initials(opponent.name))}</span><b>${context.escapeHtml(opponent.name)}</b><strong>${opponent.score.toLocaleString()}</strong><small>${opponent.incomingFrost ? `❄ ${opponent.incomingFrost} queued` : "Tap to target"}</small></button>`).join("") || `<p class="hexchain-last-standing">${context.state.playerOrder.length === 1 ? "Solo run · survive the freeze and set your high score." : "No rivals remain."}</p>`}
    </div>`}
    <div class="hexchain-arena">
      <aside class="hexchain-stat-stack">
        <div><span>SCORE</span><strong>${player.score.toLocaleString()}</strong><small>${lastGain ? `+${lastGain.toLocaleString()} last chain` : "Total points"}</small></div>
        <div><span>CHAINS</span><strong>${player.chains}</strong></div>
        <div><span>BEST</span><strong>${player.bestChain || "—"}</strong></div>
        <div class="${multiplier(player) > 1 ? "is-hot" : ""}"><span>STREAK</span><strong>${multiplier(player)}×</strong></div>
      </aside>
      <div class="hexchain-board-wrap">
        <canvas id="hexchain-board" width="742" height="680" aria-label="Your Hexchain board. Drag through three or more adjacent gems of one color. Untouched cells show a two-turn warning, then a one-turn warning, and freeze after seven chains."></canvas>
        <div class="hexchain-chain-callout" id="hexchain-chain-callout" aria-live="polite"></div>
        <div class="hexchain-achievement-toast" id="hexchain-achievement-toast" aria-live="polite"></div>
        <div class="hexchain-path-label" id="hexchain-path-label">Drag through 3+ matching gems</div>
        ${player.stuck && player.alive ? "<div class=\"hexchain-stuck-notice\"><strong>No chains available</strong><span>Use your emergency shuffle to stay in the siege.</span></div>" : ""}
        ${player.alive ? "" : "<div class=\"hexchain-frozen-overlay\"><b>❄</b><strong>FROZEN OUT</strong><span>Watch the remaining siege</span></div>"}
      </div>
      <aside class="hexchain-attack-stack">
        <div class="hexchain-incoming ${player.incomingFrost ? "is-danger" : ""}"><span>INCOMING</span><strong>❄ ${player.incomingFrost}</strong><small>${player.incomingFrost ? "Next chain counters length − 3; rest lands" : "Board is clear"}</small></div>
        <button id="hexchain-shuffle" class="hexchain-shuffle" ${player.shuffleLeft && player.alive ? "" : "disabled"}><span>↻</span><b>SHUFFLE</b><small>${player.shuffleLeft ? "1 left · frozen stay locked" : "Used"}</small></button>
      </aside>
    </div>
    ${freezeCountdownHtml(player)}
    <div class="hexchain-event" aria-live="polite"><span>✦</span><strong>${context.escapeHtml(context.state.lastEvent?.message || "The siege has begun!")}</strong></div>
    <p class="hexchain-battle-hint"><b>3+</b> clears · <b>4+</b> wipes that color · <b>5+</b> builds multipliers · <b>6+</b> resets that color · <b>7+</b> resets all tiles · untouched cells freeze after <b>7</b> chains</p>
    ${instructionsHtml(context.escapeHtml(context.roomCode))}
  </section>`;
}

export function bindHexchainGame(context: HexchainContext) {
  bindInstructions();
  const player = context.state.players[context.playerId];
  if (!player) return;
  const time = document.querySelector<HTMLElement>("#hexchain-time");
  const updateTime = () => { if (time) time.textContent = formatTime(context.state.deadline); };
  updateTime();
  if (context.state.deadline) countdownTimer = window.setInterval(updateTime, 200);
  document.querySelectorAll<HTMLButtonElement>("[data-hexchain-target]").forEach((button) => button.addEventListener("click", () => {
    selectedTargetId = button.dataset.hexchainTarget || "";
    document.querySelectorAll("[data-hexchain-target]").forEach((candidate) => candidate.classList.toggle("is-targeted", candidate === button));
    context.tap();
  }));
  document.querySelector("#hexchain-shuffle")?.addEventListener("click", () => { context.tap(); context.send({ type: "hexchain_shuffle" }); });
  const canvas = document.querySelector<HTMLCanvasElement>("#hexchain-board");
  if (canvas) bindBoard(canvas, player, context);
}

function formatTime(deadline: number | null) {
  if (!deadline) return "NO LIMIT";
  const remaining = Math.max(0, deadline - Date.now());
  const seconds = Math.ceil(remaining / 1_000);
  return `${Math.floor(seconds / 60)}:${String(seconds % 60).padStart(2, "0")}`;
}

function bindBoard(canvas: HTMLCanvasElement, player: HexchainPlayerView, context: HexchainContext) {
  const drawing = { active: false, path: [] as number[], color: null as number | null };
  const label = document.querySelector<HTMLElement>("#hexchain-path-label");
  const event = context.state.lastEvent;
  // Event ids restart at 1 in every room. Include the room in the key so a player
  // returning for another match does not have its early clear animations skipped.
  const animationKey = `${context.roomCode}:player:${player.id}`;
  const shouldAnimate = Boolean(event?.animation && event.playerId === player.id && animatedEvents.get(animationKey) !== event.id);
  let locked = shouldAnimate;
  const draw = () => drawBoard(canvas, player.board, drawing.path, performance.now(), player.chains);
  if (shouldAnimate && event?.animation) {
    animatedEvents.set(animationKey, event.id);
    runBoardAnimation(canvas, player, event, context, () => { locked = false; });
  } else draw();
  if (!player.alive) return;
  const tileAt = (event: PointerEvent) => {
    const rect = canvas.getBoundingClientRect();
    const x = (event.clientX - rect.left) * canvas.width / rect.width;
    const y = (event.clientY - rect.top) * canvas.height / rect.height;
    let closest = -1;
    let distance = Infinity;
    CELLS.forEach((cell, index) => {
      const next = Math.hypot(x - cell.x, y - cell.y);
      if (next < cell.radius && next < distance) { closest = index; distance = next; }
    });
    return closest;
  };
  const begin = (event: PointerEvent) => {
    if (locked) return;
    const index = tileAt(event);
    if (index < 0 || player.board[index].frozen) return;
    drawing.active = true;
    beginHexchainGesture();
    drawing.path = [index];
    drawing.color = player.board[index].special === "wild" ? null : player.board[index].color;
    context.tone(640, .04, "triangle", 0, .08);
    canvas.setPointerCapture(event.pointerId);
    draw();
  };
  const move = (event: PointerEvent) => {
    if (!drawing.active || locked) return;
    const index = tileAt(event);
    if (index < 0 || player.board[index].frozen) return;
    const last = drawing.path.at(-1)!;
    if (drawing.path.length > 1 && index === drawing.path.at(-2)) {
      drawing.path.pop();
      drawing.color = drawing.path.map((pathIndex) => player.board[pathIndex]).find((tile) => tile.special !== "wild")?.color ?? null;
    } else if (index !== last && !drawing.path.includes(index) && ADJACENCY[last].includes(index)) {
      const tile = player.board[index];
      if (tile.special === "wild" || drawing.color === null || tile.color === drawing.color) {
        drawing.path.push(index);
        if (drawing.color === null && tile.special !== "wild") drawing.color = tile.color;
        context.tone(600 + drawing.path.length * 40, .04, "triangle", 0, .08);
      }
    }
    if (label) label.textContent = drawing.path.length >= 3 ? `${drawing.path.length}-chain · release to link` : "Keep linking…";
    draw();
  };
  const settleGesture = () => {
    if (endHexchainGesture()) context.refreshAfterGesture();
  };
  const finish = () => {
    if (!drawing.active) return;
    drawing.active = false;
    if (drawing.path.length >= 3) {
      context.send({ type: "hexchain_chain", path: drawing.path, targetPlayerId: selectedTargetId || undefined });
      context.tap();
    } else {
      context.tone(150, .15, "square", 0, .08);
      context.tone(157, .15, "square", 0, .08);
    }
    drawing.path = [];
    drawing.color = null;
    if (label) label.textContent = "Drag through 3+ matching gems";
    draw();
    settleGesture();
  };
  const cancel = () => {
    if (!drawing.active) return;
    drawing.active = false;
    drawing.path = [];
    drawing.color = null;
    if (label) label.textContent = "Drag through 3+ matching gems";
    draw();
    settleGesture();
  };
  canvas.addEventListener("pointerdown", begin);
  canvas.addEventListener("pointermove", move);
  canvas.addEventListener("pointerup", finish);
  canvas.addEventListener("pointercancel", cancel);
}

function hexPath(context: CanvasRenderingContext2D, x: number, y: number, radius: number) {
  const points = Array.from({ length: 6 }, (_, index) => {
    const angle = -Math.PI / 2 + index * Math.PI / 3;
    return { x: x + Math.cos(angle) * radius, y: y + Math.sin(angle) * radius };
  });
  const rounding = Math.min(8, radius * .17);
  const toward = (from: { x: number; y: number }, to: { x: number; y: number }) => {
    const distance = Math.hypot(to.x - from.x, to.y - from.y) || 1;
    return { x: from.x + (to.x - from.x) * rounding / distance, y: from.y + (to.y - from.y) * rounding / distance };
  };
  context.beginPath();
  const start = toward(points[0], points[5]);
  context.moveTo(start.x, start.y);
  points.forEach((point, index) => {
    const previous = points[(index + 5) % 6];
    const next = points[(index + 1) % 6];
    const entry = toward(point, previous);
    const exit = toward(point, next);
    context.lineTo(entry.x, entry.y);
    context.quadraticCurveTo(point.x, point.y, exit.x, exit.y);
  });
  context.closePath();
}

type TileTransform = { x?: number; y?: number; scale?: number; opacity?: number; rotation?: number; selected?: boolean; highlighted?: boolean };

function freezeAge(_chains: number) {
  return HEXCHAIN_FREEZE_AGE;
}

function drawTile(context: CanvasRenderingContext2D, tile: HexchainTile, index: number, time: number, chains: number, transform: TileTransform = {}) {
  const cell = CELLS[index];
  if (!cell) return;
  const scale = transform.scale ?? 1;
  const opacity = transform.opacity ?? 1;
  if (scale <= .01 || opacity <= .01) return;
  const color = COLORS[tile.color];
  context.save();
  context.translate(cell.x + (transform.x || 0), cell.y + (transform.y || 0));
  context.rotate(transform.rotation || 0);
  context.scale(scale, scale);
  context.globalAlpha = opacity;
  const selected = Boolean(transform.selected);
  const highlighted = Boolean(transform.highlighted);

  context.shadowColor = selected || highlighted ? color[0] : "rgba(0,0,0,.52)";
  context.shadowBlur = selected ? 30 : highlighted ? 24 : 11;
  context.shadowOffsetY = selected ? 0 : 7;
  hexPath(context, 0, selected ? -5 : 0, cell.radius - 2);
  const tileY = selected ? -5 : 0;
  if (tile.special === "wild") {
    context.save();
    context.clip();
    for (let wedge = 0; wedge < COLORS.length; wedge += 1) {
      const startAngle = wedge * Math.PI / 3 - Math.PI / 2;
      context.beginPath();
      context.moveTo(0, tileY);
      context.arc(0, tileY, cell.radius + 2, startAngle, startAngle + Math.PI / 3);
      context.closePath();
      context.fillStyle = COLORS[wedge][2];
      context.fill();
    }
    const wildGlow = context.createRadialGradient(0, tileY, 0, 0, tileY, cell.radius * .72);
    wildGlow.addColorStop(0, selected ? "rgba(255,255,255,.72)" : "rgba(255,255,255,.34)");
    wildGlow.addColorStop(1, "rgba(255,255,255,0)");
    context.fillStyle = wildGlow;
    context.fillRect(-cell.radius, tileY - cell.radius, cell.radius * 2, cell.radius * 2);
    context.restore();
  } else {
    const gradient = context.createRadialGradient(-13, tileY - 14, 1, 0, tileY, cell.radius);
    if (selected) {
      gradient.addColorStop(0, "#fff");
      gradient.addColorStop(.26, color[0]);
      gradient.addColorStop(.62, color[2]);
      gradient.addColorStop(1, color[0]);
    } else {
      gradient.addColorStop(0, color[0]);
      gradient.addColorStop(.5, color[2]);
      gradient.addColorStop(1, color[1]);
    }
    context.fillStyle = gradient;
    context.fill();
  }
  context.shadowBlur = 0;
  context.shadowOffsetY = 0;

  context.save();
  hexPath(context, 0, selected ? -5 : 0, cell.radius - 3);
  context.clip();
  context.beginPath();
  context.ellipse(-8, (selected ? -5 : 0) - 24, 28, 12, -.3, 0, Math.PI * 2);
  context.fillStyle = selected ? "rgba(255,255,255,.27)" : "rgba(255,255,255,.13)";
  context.fill();
  context.restore();

  hexPath(context, 0, selected ? -5 : 0, cell.radius - 2);
  const wildPulse = .22 + .13 * Math.sin(time * .003);
  context.strokeStyle = selected ? "rgba(255,255,255,.88)" : highlighted ? "rgba(255,255,255,.7)" : tile.special === "wild" ? `rgba(255,255,255,${wildPulse})` : "rgba(255,255,255,.1)";
  context.lineWidth = selected ? 4 : highlighted ? 3 : 2;
  context.stroke();

  if (tile.special === "wild") {
    context.save();
    context.font = "900 22px system-ui";
    context.textAlign = "center";
    context.textBaseline = "middle";
    context.fillStyle = "rgba(255,255,255,.94)";
    context.shadowColor = "#fff";
    context.shadowBlur = 10;
    context.fillText("✦", 0, tileY);
    context.restore();
  }

  const threshold = freezeAge(chains);
  const movesLeft = threshold - tile.age;
  if (tile.special !== "wild" && !tile.frozen && movesLeft <= 2 && movesLeft > 0) {
    hexPath(context, 0, selected ? -5 : 0, cell.radius - 3);
    context.fillStyle = `rgba(15,18,30,${movesLeft === 2 ? .17 : .26})`;
    context.fill();
    context.strokeStyle = color[0];
    context.globalAlpha = movesLeft === 1 ? .92 : .58;
    context.lineWidth = movesLeft === 1 ? 4 : 3;
    context.stroke();
    context.globalAlpha = opacity;
    context.beginPath();
    context.arc(0, tileY, movesLeft === 1 ? 24 : 22, 0, Math.PI * 2);
    context.fillStyle = movesLeft === 1 ? "rgba(68,15,25,.78)" : "rgba(10,16,27,.68)";
    context.fill();
    context.font = `950 ${movesLeft === 1 ? 40 : 36}px system-ui`;
    context.textAlign = "center";
    context.textBaseline = "middle";
    context.lineWidth = 7;
    context.strokeStyle = "rgba(9,10,17,.72)";
    context.strokeText(String(movesLeft), 0, tileY);
    context.fillStyle = "#fff";
    context.shadowColor = color[0];
    context.shadowBlur = movesLeft === 1 ? 15 : 10;
    context.fillText(String(movesLeft), 0, tileY);
    context.shadowBlur = 0;
  }
  if (tile.special !== "wild" && tile.frozen) {
    hexPath(context, 0, selected ? -5 : 0, cell.radius - 3);
    context.fillStyle = "rgba(220,246,255,.62)";
    context.fill();
    context.strokeStyle = "#e9fbff";
    context.globalAlpha = opacity;
    context.lineWidth = 5;
    context.stroke();
    context.globalAlpha = opacity;
    context.save();
    context.strokeStyle = "#8edfff";
    context.globalAlpha = .8;
    context.lineWidth = 3;
    for (let arm = 0; arm < 6; arm += 1) {
      const angle = arm * Math.PI / 3;
      context.beginPath();
      context.moveTo(Math.cos(angle) * 17, (selected ? -5 : 0) + Math.sin(angle) * 17);
      context.lineTo(Math.cos(angle) * 33, (selected ? -5 : 0) + Math.sin(angle) * 33);
      context.stroke();
    }
    context.restore();
    context.font = "900 30px 'Segoe UI Symbol', system-ui";
    context.textAlign = "center";
    context.textBaseline = "middle";
    context.fillStyle = "#fff";
    context.shadowColor = "#52cfff";
    context.shadowBlur = 18;
    context.fillText("❄", 0, selected ? -5 : 0);
  } else {
    const sparkle = ((time * .000125 + ((index * 17 + tile.color * 31) % 97) / 97) % 1);
    if (sparkle < .035) {
      const alpha = Math.sin(sparkle / .035 * Math.PI) * .7;
      context.save();
      context.globalAlpha = opacity * alpha;
      context.translate(-13, (selected ? -5 : 0) - 18);
      context.fillStyle = "#fff";
      context.shadowColor = "#fff";
      context.shadowBlur = 7;
      context.beginPath();
      context.moveTo(0, -7); context.lineTo(2, -2); context.lineTo(7, 0); context.lineTo(2, 2);
      context.lineTo(0, 7); context.lineTo(-2, 2); context.lineTo(-7, 0); context.lineTo(-2, -2); context.closePath(); context.fill();
      context.restore();
    }
  }
  context.restore();
}

function drawBurst(context: CanvasRenderingContext2D, index: number, progress: number, colorIndex: number, count = 7) {
  if (progress <= 0 || progress >= 1) return;
  const cell = CELLS[index];
  const color = COLORS[colorIndex] || COLORS[0];
  context.save();
  for (let particle = 0; particle < count; particle += 1) {
    const angle = particle / count * Math.PI * 2 + index * .43;
    const distance = 12 + progress * (32 + (particle % 3) * 8);
    const radius = (3.6 - progress * 2.5) * (particle % 2 ? .8 : 1);
    context.globalAlpha = (1 - progress) * .9;
    const x = cell.x + Math.cos(angle) * distance;
    const y = cell.y + Math.sin(angle) * distance - progress * 8;
    context.fillStyle = particle % 2 ? "#fff" : color[0];
    context.strokeStyle = context.fillStyle;
    context.shadowColor = context.fillStyle;
    context.shadowBlur = 8;
    if (particle % 3 === 0) {
      const trail = 5 + (1 - progress) * 9;
      context.lineWidth = Math.max(.8, radius * .48);
      context.beginPath();
      context.moveTo(x - Math.cos(angle) * trail, y - Math.sin(angle) * trail);
      context.lineTo(x, y);
      context.stroke();
    } else {
      context.beginPath();
      context.moveTo(x, y - radius * 1.7);
      context.lineTo(x + radius * .48, y - radius * .48);
      context.lineTo(x + radius * 1.7, y);
      context.lineTo(x + radius * .48, y + radius * .48);
      context.lineTo(x, y + radius * 1.7);
      context.lineTo(x - radius * .48, y + radius * .48);
      context.lineTo(x - radius * 1.7, y);
      context.lineTo(x - radius * .48, y - radius * .48);
      context.closePath();
      context.fill();
    }
  }
  context.restore();
}

function drawFrostImpact(context: CanvasRenderingContext2D, index: number, progress: number) {
  if (progress < 0 || progress >= 1) return;
  const cell = CELLS[index];
  const eased = 1 - Math.pow(1 - progress, 3);
  context.save();
  context.globalAlpha = 1 - progress;
  context.strokeStyle = "#dffaff";
  context.lineWidth = 8 - progress * 5;
  context.shadowColor = "#5ed8ff";
  context.shadowBlur = 28;
  context.beginPath();
  context.arc(cell.x, cell.y, 20 + eased * 48, 0, Math.PI * 2);
  context.stroke();
  for (let particle = 0; particle < 8; particle += 1) {
    const angle = particle / 8 * Math.PI * 2 + index * .31;
    const distance = 12 + eased * 62;
    const x = cell.x + Math.cos(angle) * distance;
    const y = cell.y + Math.sin(angle) * distance;
    context.fillStyle = particle % 2 ? "#fff" : "#7de2ff";
    context.font = `${18 - progress * 7}px 'Segoe UI Symbol', system-ui`;
    context.textAlign = "center";
    context.textBaseline = "middle";
    context.fillText("❄", x, y);
  }
  context.restore();
}

function drawBoard(canvas: HTMLCanvasElement, board: HexchainTile[], path: number[] = [], time = performance.now(), chains = 0, transforms = new Map<number, TileTransform>()) {
  const context = canvas.getContext("2d");
  if (!context) return;
  context.clearRect(0, 0, canvas.width, canvas.height);
  const breathe = .025 + .012 * Math.sin(time * .0008);
  context.strokeStyle = `rgba(255,255,255,${breathe})`;
  context.lineWidth = 2;
  CELLS.forEach((cell) => { hexPath(context, cell.x, cell.y, cell.radius - 1); context.stroke(); });
  board.forEach((tile, index) => drawTile(context, tile, index, time, chains, { ...transforms.get(index), selected: path.includes(index) || transforms.get(index)?.selected }));
}

function easeOutBack(value: number) {
  const c1 = 1.70158;
  const c3 = c1 + 1;
  return 1 + c3 * Math.pow(value - 1, 3) + c1 * Math.pow(value - 1, 2);
}

function easeOutBounce(value: number) {
  const n1 = 7.5625;
  const d1 = 2.75;
  if (value < 1 / d1) return n1 * value * value;
  if (value < 2 / d1) return n1 * (value -= 1.5 / d1) * value + .75;
  if (value < 2.5 / d1) return n1 * (value -= 2.25 / d1) * value + .9375;
  return n1 * (value -= 2.625 / d1) * value + .984375;
}

function easeOutCubic(value: number) {
  return 1 - Math.pow(1 - value, 3);
}

function transitionTransforms(transition: HexchainBoardTransition, progress: number, shuffle = false) {
  const transforms = new Map<number, TileTransform>();
  const eased = shuffle ? easeOutBack(progress) : easeOutBounce(progress);
  transition.moved.forEach(({ from, to }) => {
    const source = CELLS[from];
    const destination = CELLS[to];
    if (!source || !destination) return;
    transforms.set(to, {
      x: (source.x - destination.x) * (1 - eased),
      y: (source.y - destination.y) * (1 - eased) - (shuffle ? Math.sin(progress * Math.PI) * 18 : 0),
      scale: shuffle ? .82 + .18 * eased : 1,
      opacity: .45 + .55 * progress,
      rotation: shuffle ? (1 - progress) * (to % 2 ? -.12 : .12) : 0,
    });
  });
  transition.spawnedIndexes.forEach((index, order) => {
    const local = Math.max(0, Math.min(1, progress * 1.25 - order * .025));
    transforms.set(index, { y: -48 * (1 - easeOutBack(local)), scale: Math.max(0, easeOutBack(local)), opacity: local });
  });
  return transforms;
}

function clearTransforms(indexes: number[], elapsed: number, stagger: number, duration: number) {
  const transforms = new Map<number, TileTransform>();
  indexes.forEach((index, order) => {
    const progress = Math.max(0, Math.min(1, (elapsed - order * stagger) / duration));
    let scale = 1;
    let opacity = 1;
    if (progress < .2) scale = 1 + .15 * easeOutCubic(progress / .2);
    else if (progress < .5) scale = 1.15 - .05 * ((progress - .2) / .3);
    else {
      const eased = easeOutCubic((progress - .5) / .5);
      scale = 1.1 * (1 - .3 * eased);
      opacity = 1 - eased;
    }
    transforms.set(index, { scale, opacity, selected: true });
  });
  return transforms;
}

function chainCelebration(length: number, eventId: number, points: number) {
  const wordIndex = Math.abs(eventId * 17 + length * 31) % CHAIN_WORDS.length;
  if (length >= 20 || points >= 10_000_000) return { label: CHAIN_WORDS[wordIndex], mode: "tier-godlike", duration: 4_000, achievement: "GODLIKE!" };
  if (length >= 15 || points >= 5_000_000) return { label: CHAIN_WORDS[wordIndex], mode: "tier-mega", duration: 3_500, achievement: "MEGA CHAIN!" };
  if (length >= 8) return { label: CHAIN_WORDS[wordIndex], mode: "tier-legendary", duration: 2_500 };
  if (length >= 6) return { label: CHAIN_WORDS[wordIndex], mode: "tier-epic", duration: 2_000 };
  return { label: CHAIN_WORDS[wordIndex], mode: "", duration: length >= 4 ? 1_600 : 1_200 };
}

function scheduleBoardMessage(callback: () => void, delay: number) {
  const timer = window.setTimeout(() => {
    boardMessageTimers.delete(timer);
    callback();
  }, delay);
  boardMessageTimers.add(timer);
}

function showChainCallout(text: string, detail = "", mode = "", duration = 1_600) {
  const callout = document.querySelector<HTMLElement>("#hexchain-chain-callout");
  if (!callout) return;
  if (chainCalloutTimer !== null) window.clearTimeout(chainCalloutTimer);
  callout.className = "hexchain-chain-callout";
  void callout.offsetWidth;
  callout.className = `hexchain-chain-callout is-visible ${mode}`;
  callout.innerHTML = `${text}<span>${detail}</span>`;
  chainCalloutTimer = window.setTimeout(hideChainCallout, duration);
}

function hideChainCallout() {
  const callout = document.querySelector<HTMLElement>("#hexchain-chain-callout");
  if (callout) callout.className = "hexchain-chain-callout";
  if (chainCalloutTimer !== null) window.clearTimeout(chainCalloutTimer);
  chainCalloutTimer = null;
}

function showAchievementToast(text: string, detail = "", mode = "", duration = 1_700) {
  const toast = document.querySelector<HTMLElement>("#hexchain-achievement-toast");
  if (!toast) return;
  if (achievementToastTimer !== null) window.clearTimeout(achievementToastTimer);
  toast.className = "hexchain-achievement-toast";
  void toast.offsetWidth;
  toast.className = `hexchain-achievement-toast is-visible ${mode}`;
  toast.innerHTML = `<strong>${text}</strong>${detail ? `<span>${detail}</span>` : ""}`;
  achievementToastTimer = window.setTimeout(() => {
    toast.className = "hexchain-achievement-toast";
    achievementToastTimer = null;
  }, duration);
}

function resetAchievement(animation: HexchainAnimation) {
  const frozenCount = animation.resetIndexes.filter((index) => animation.before[index]?.frozen).length;
  const resetCount = animation.resetIndexes.length - frozenCount;
  if (animation.resetKind === "all") {
    return {
      title: "BOARD RESET!",
      detail: frozenCount > 0
        ? `Board thaw! ${frozenCount} ${frozenCount === 1 ? "tile" : "tiles"} unfrozen · all counters reset!`
        : "All tile counters reset!",
    };
  }
  if (frozenCount > 0 && resetCount > 0) return { title: "COLOR RESET!", detail: `Thawed ${frozenCount} & reset ${resetCount} tiles!` };
  if (frozenCount > 0) return { title: "COLOR RESET!", detail: frozenCount === 1 ? "Thawed a frozen tile!" : `Thawed ${frozenCount} frozen tiles!` };
  return { title: "COLOR RESET!", detail: `Reset ${resetCount} ${resetCount === 1 ? "tile" : "tiles"}!` };
}

function runBoardAnimation(canvas: HTMLCanvasElement, player: HexchainPlayerView, event: NonNullable<HexchainView["lastEvent"]>, context: HexchainContext, onDone: () => void) {
  const animation = event.animation!;
  const path = event.path || animation.primary.clearedIndexes;
  const isShuffle = event.kind === "shuffle";
  const clearDuration = isShuffle ? 0 : 450 + Math.max(0, path.length - 1) * 65;
  const settleDuration = isShuffle ? 680 : 500;
  const highlightDuration = animation.wipe ? 700 : 0;
  const wipeClearDuration = animation.wipe ? 450 + Math.max(0, animation.wipe.clearedIndexes.length - 1) * 25 : 0;
  const wipeSettleDuration = animation.wipe ? 500 : 0;
  const baseDuration = clearDuration + settleDuration + highlightDuration + wipeClearDuration + wipeSettleDuration;
  const frostDuration = animation.frost ? 700 + Math.min(800, animation.frost.landedIndexes.length * 35) : 0;
  const totalDuration = baseDuration + frostDuration;
  const started = performance.now();
  beginHexchainAnimation();
  if (isShuffle) {
    showChainCallout("Board shuffled!", "Frozen tiles stayed locked", "is-shuffle");
    Array.from({ length: 8 }, (_, index) => 300 + ((index * 137 + event.id * 53) % 600)).forEach((frequency, index) => context.tone(frequency, .06, "triangle", index * .035, .06));
  }
  else {
    const points = event.gained || event.baseGained || 0;
    const celebration = chainCelebration(path.length, event.id, points);
    const multiplierText = player.fiveStreak >= 2 ? ` · ${player.fiveStreak}×` : "";
    const scoreBreakdown = event.wipeBonus
      ? `${(event.baseGained || 0).toLocaleString()} chain + ${event.wipeBonus.toLocaleString()} wipe`
      : `${path.length}-chain`;
    showChainCallout(celebration.label, `+${points.toLocaleString()} total · ${scoreBreakdown}${multiplierText}`, celebration.mode, celebration.duration);
    if (celebration.achievement) {
      scheduleBoardMessage(() => showAchievementToast(celebration.achievement!, "Massive chain bonus", celebration.mode, 3_000), 180);
    } else if (path.length >= 5 && player.fiveStreak >= 2) {
      scheduleBoardMessage(() => showAchievementToast(`${player.fiveStreak}x STREAK!`, "Consecutive 5+ chains", "is-streak"), 180);
    }
    if (event.previousBestChain !== undefined && event.previousBestChain > 0 && path.length > event.previousBestChain) {
      scheduleBoardMessage(() => context.showToast(`New best chain: ${path.length}!`), 400);
    }
    if (animation.resetKind === "color" || animation.resetKind === "all") {
      const reset = resetAchievement(animation);
      scheduleBoardMessage(() => showAchievementToast(reset.title, reset.detail, "is-reset", 2_200), Math.max(420, clearDuration - 40));
    }
    if (event.landed) {
      const countered = event.cancelled ? ` · ${event.cancelled} countered` : "";
      scheduleBoardMessage(() => showAchievementToast("FROST LANDED!", `${event.landed} ${event.landed === 1 ? "tile froze" : "tiles froze"}${countered}`, "is-reset", 2_200), Math.max(420, baseDuration + 80));
      context.noise(.72, .16, 4_500, 900, 180, baseDuration / 1_000);
      animation.frost?.landedIndexes.slice(0, 12).forEach((_, index) => context.tone(900 - index * 35, .14, "triangle", baseDuration / 1_000 + index * .045, .055));
    }
    context.tone(880, .12, "sine", 0, .08);
    context.tone(1_320, .1, "triangle", 0, .05);
    const semitones = [0, 2, 4, 7, 9, 12, 14, 16, 19, 21, 24];
    path.forEach((_, index) => {
      const semitone = semitones[Math.min(index, semitones.length - 1)];
      const frequency = 440 * Math.pow(2, semitone / 12);
      context.tone(frequency, .25, "sine", .135 + index * .065, Math.min(.25, .12 + index / path.length * .08));
    });
    if (path.length >= 5 && player.fiveStreak >= 2) context.sweep(200, 800 + player.fiveStreak * 200, .35, "sawtooth", .02, .1, 400, 3_000);
    if (path.length >= 15) {
      [523.25, 659.25, 783.99].forEach((frequency) => context.tone(frequency, .6, "sawtooth", .08, .08));
      context.tone(1_046.5, .4, "triangle", .43, .12);
    }
    if (animation.wipe) context.noise(.6, .12, 300, 4_000, 500, (clearDuration + settleDuration) / 1_000);
  }

  const frame = (time: number) => {
    if (!canvas.isConnected) return;
    const elapsed = time - started;
    const context2d = canvas.getContext("2d");
    if (!context2d) return;
    if (!isShuffle && elapsed < clearDuration) {
      const transforms = clearTransforms(path, elapsed, 65, 450);
      drawBoard(canvas, animation.before, [], time, player.chains, transforms);
      path.forEach((index, order) => {
        const progress = Math.max(0, Math.min(1, (elapsed - order * 65) / 450));
        drawBurst(context2d, index, progress, animation.before[index]?.color || 0);
      });
      if (path.length >= 6 && elapsed < 260) {
        context2d.fillStyle = `rgba(255,255,255,${(1 - elapsed / 260) * .12})`;
        context2d.fillRect(0, 0, canvas.width, canvas.height);
      }
      animation.resetIndexes.forEach((index) => drawBurst(context2d, index, Math.min(1, elapsed / clearDuration), animation.before[index]?.color || 0, 5));
    } else if (elapsed < clearDuration + settleDuration) {
      const progress = Math.max(0, Math.min(1, (elapsed - clearDuration) / settleDuration));
      drawBoard(canvas, animation.primary.board, [], time, player.chains, transitionTransforms(animation.primary, progress, isShuffle));
    } else if (animation.wipe && elapsed < clearDuration + settleDuration + highlightDuration) {
      const phaseTime = elapsed - clearDuration - settleDuration;
      const transforms = new Map<number, TileTransform>();
      animation.wipe.highlightedIndexes.forEach((index) => transforms.set(index, { scale: 1.04 + Math.sin(phaseTime * .022) * .055, highlighted: true }));
      drawBoard(canvas, animation.primary.board, [], time, player.chains, transforms);
      showChainCallout("COLOR WIPE!", `${animation.wipe.highlightedIndexes.length} more gems · +${(event.wipeBonus || 0).toLocaleString()}`, "is-wipe", 1_700);
    } else if (animation.wipe && elapsed < clearDuration + settleDuration + highlightDuration + wipeClearDuration) {
      const phaseTime = elapsed - clearDuration - settleDuration - highlightDuration;
      const transforms = clearTransforms(animation.wipe.clearedIndexes, phaseTime, 25, 450);
      drawBoard(canvas, animation.primary.board, [], time, player.chains, transforms);
      animation.wipe.clearedIndexes.forEach((index, order) => {
        const progress = Math.max(0, Math.min(1, (phaseTime - order * 25) / 450));
        drawBurst(context2d, index, progress, animation.primary.board[index]?.color || animation.chainColor || 0, 5);
      });
    } else if (animation.wipe && elapsed < baseDuration) {
      const progress = Math.max(0, Math.min(1, (elapsed - clearDuration - settleDuration - highlightDuration - wipeClearDuration) / wipeSettleDuration));
      drawBoard(canvas, animation.wipe.board, [], time, player.chains, transitionTransforms(animation.wipe, progress));
    } else if (animation.frost) {
      const phaseTime = elapsed - baseDuration;
      const frostBoard = animation.frost.before.map((tile) => ({ ...tile }));
      animation.frost.landedIndexes.forEach((index, order) => {
        const progress = (phaseTime - order * 35) / 560;
        if (progress >= 0) frostBoard[index] = { ...animation.frost!.after[index] };
      });
      drawBoard(canvas, frostBoard, [], time, player.chains);
      animation.frost.landedIndexes.forEach((index, order) => drawFrostImpact(context2d, index, (phaseTime - order * 35) / 560));
      if (phaseTime < 180) {
        context2d.fillStyle = `rgba(190,240,255,${(1 - phaseTime / 180) * .22})`;
        context2d.fillRect(0, 0, canvas.width, canvas.height);
      }
    }

    if (elapsed < totalDuration) {
      boardAnimationFrame = window.requestAnimationFrame(frame);
      return;
    }
    boardAnimationFrame = null;
    drawBoard(canvas, player.board, [], time, player.chains);
    onDone();
    if (endHexchainAnimation()) context.refreshAfterGesture();
  };
  boardAnimationFrame = window.requestAnimationFrame(frame);
}

export function hexchainDisplayHtml(context: HexchainContext) {
  cleanupHexchain();
  const contestants = context.state.playerOrder.map((id) => context.state.players[id]).filter(Boolean);
  return `<section class="hexchain-display">
    <header><div><p class="eyebrow">${context.state.deadline ? "LIVE FROST BATTLE" : "SOLO HIGH-SCORE RUN"}</p><h1>Hexchain Siege</h1></div><div class="hexchain-timer"><span>${context.state.deadline ? "TIME" : "SOLO"}</span><strong id="hexchain-time">${formatTime(context.state.deadline)}</strong></div></header>
    <div class="hexchain-display-grid">${contestants.map((player, index) => `<article class="${player.alive ? "" : "is-eliminated"}"><div><span class="hexchain-avatar hexchain-avatar--${index % 6}">${context.escapeHtml(context.initials(player.name))}</span><h2>${context.escapeHtml(player.name)}</h2><strong>${player.score.toLocaleString()}</strong></div><canvas data-hexchain-display-board="${player.id}" width="742" height="680"></canvas><footer><span>${player.chains} chains · best ${player.bestChain || "—"}</span><b>${player.incomingFrost ? `❄ ${player.incomingFrost} incoming` : player.alive ? "CLEAR" : "FROZEN OUT"}</b></footer></article>`).join("")}</div>
    <div class="hexchain-event"><span>✦</span><strong>${context.escapeHtml(context.state.lastEvent?.message || "The siege has begun!")}</strong></div>
  </section>`;
}

export function bindHexchainDisplay(context: HexchainContext) {
  document.querySelectorAll<HTMLCanvasElement>("[data-hexchain-display-board]").forEach((canvas) => {
    const player = context.state.players[canvas.dataset.hexchainDisplayBoard || ""];
    if (!player) return;
    const event = context.state.lastEvent;
    const animationKey = `${context.roomCode}:display:${player.id}`;
    if (event?.animation && event.playerId === player.id && animatedEvents.get(animationKey) !== event.id && boardAnimationFrame === null) {
      animatedEvents.set(animationKey, event.id);
      runBoardAnimation(canvas, player, event, context, () => undefined);
    } else drawBoard(canvas, player.board, [], performance.now(), player.chains);
  });
  const time = document.querySelector<HTMLElement>("#hexchain-time");
  const update = () => { if (time) time.textContent = formatTime(context.state.deadline); };
  update();
  if (context.state.deadline) countdownTimer = window.setInterval(update, 200);
}
